/**
 * Sales overview tab — the original single-page Sales Dashboard body, moved
 * verbatim when the dashboard grew tabs (Overview | Tentpole Calendar).
 * Purely presentational: data/loaders/auto-refresh stay in SalesDashboard.
 */

import React from 'react';
import { Card, KpiCard } from '../ui';
import { SalesOverviewResponse, IoDeliveryResponse } from '../../services/salesApi';
import RfpsInFlight from './RfpsInFlight';
import IoDeliveryHealth from './IoDeliveryHealth';
import FollowUpQueue from './FollowUpQueue';
import RecentActivity from './RecentActivity';

// null = unknown (no data); true = good (no under-pacers); false = bad (some under-pacers)
const liveIosTrend = (io: IoDeliveryResponse | null): boolean | null => {
  if (!io) return null;
  return io.totals.underPacing === 0;
};

interface OverviewTabProps {
  overview: SalesOverviewResponse | null;
  overviewError: string | null;
  overviewLoading: boolean;
  ioDelivery: IoDeliveryResponse | null;
  ioError: string | null;
  ioLoading: boolean;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  overview,
  overviewError,
  overviewLoading,
  ioDelivery,
  ioError,
  ioLoading,
}) => (
  <div style={styles.stack}>
    {/* KPI strip */}
    <section style={styles.kpiRow}>
      <KpiCard
        label="RFPs in flight"
        value={overviewLoading ? '—' : overview?.rfpStats.total ?? 0}
        delta={overview ? `${overview.rfpStats.last7Days} in last 7 days` : undefined}
        up={overview && overview.rfpStats.last7Days > 0 ? true : null}
      />
      <KpiCard
        label="Distinct advertisers"
        value={overviewLoading ? '—' : overview?.rfpStats.distinctAdvertisers ?? 0}
        compareLabel="across in-flight RFPs"
      />
      <KpiCard
        label="Follow-ups awaiting action"
        value={overviewLoading ? '—' : (overview?.followUpQueue.typeACount ?? 0) + (overview?.followUpQueue.typeBCount ?? 0)}
        delta={
          overview
            ? `${overview.followUpQueue.typeACount} awaiting reply · ${overview.followUpQueue.typeBCount} need response`
            : undefined
        }
        up={null}
      />
      <KpiCard
        label="Live IOs"
        value={ioLoading ? '—' : ioDelivery?.totals.delivering ?? 0}
        delta={
          ioDelivery
            ? `${ioDelivery.totals.underPacing} under · ${ioDelivery.totals.endingSoon} ending · ${ioDelivery.totals.launchingSoon} launching ≤7d`
            : undefined
        }
        up={liveIosTrend(ioDelivery)}
        live={!!ioDelivery && ioDelivery.totals.delivering > 0}
      />
    </section>

    {/* Two-column on wide screens, stack on narrow */}
    <div style={styles.twoCol}>
      <Card style={styles.widgetCard}>
        <RfpsInFlight
          loading={overviewLoading}
          error={overviewError}
          rfps={overview?.rfpsInFlight ?? []}
        />
      </Card>
      <Card style={styles.widgetCard}>
        <FollowUpQueue
          loading={overviewLoading}
          error={overviewError}
          summary={overview?.followUpQueue ?? null}
        />
      </Card>
    </div>

    <Card style={styles.widgetCard}>
      <IoDeliveryHealth
        loading={ioLoading}
        error={ioError}
        data={ioDelivery}
      />
    </Card>

    <Card style={styles.widgetCard}>
      <RecentActivity
        loading={overviewLoading}
        error={overviewError}
        events={overview?.recentActivity ?? []}
      />
    </Card>

    {overview?.generatedAt && (
      <div style={styles.footnote}>
        Overview generated {new Date(overview.generatedAt).toLocaleString()}
        {ioDelivery?.generatedAt && ` · IO data ${new Date(ioDelivery.generatedAt).toLocaleString()}`}
        {' · auto-refreshes every 5 min while tab is active'}
      </div>
    )}
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 12,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    gap: 16,
  },
  widgetCard: {
    padding: 0,
  },
};

export default OverviewTab;
